import React, { useCallback, useEffect, useState } from "react";

import axios from "axios";
import { LuConstruction, LuRefreshCw, LuX } from "react-icons/lu";

import { config } from "@shared/config";
import { Button } from "@shared/components/ui/Button";
import { defaultHeaders } from "@shared/features/api/utils";

import {
  type MaintenanceStatus,
  getMaintenanceBypass,
  maintenanceBypassHeaders,
  useMaintenanceStore,
} from "./maintenance-store";

const POLL_INTERVAL = 30_000;
const BANNER_DISMISSED_KEY = "maintenanceBannerDismissed";

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

/** Resolves to null when the API cannot be reached: nothing to show then. */
const fetchMaintenanceStatus = async (): Promise<MaintenanceStatus | null> => {
  try {
    const { data } = await axios.get<MaintenanceStatus>(
      `${config.VITE_MC_API.replace(/\/+$/, "")}/maintenance`,
      {
        headers: { ...defaultHeaders(), ...maintenanceBypassHeaders() },
        params: { t: Date.now() },
      }
    );

    return typeof data?.active === "boolean" ? data : null;
  } catch {
    return null;
  }
};

const isUpcoming = (status: MaintenanceStatus | null) =>
  !!status &&
  !status.active &&
  !!status.startsAt &&
  new Date(status.until ?? status.startsAt).getTime() > Date.now();

const MaintenancePage: React.FC<{
  status: MaintenanceStatus;
  onRetry: () => void;
  retrying: boolean;
}> = ({ status, onRetry, retrying }) => (
  <div
    role="alert"
    className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-white p-6 text-center"
  >
    <LuConstruction className="size-14 text-primary-600" />
    <div className="flex max-w-md flex-col gap-2">
      <h1 className="text-2xl font-medium text-gray-900">
        Maintenance en cours
      </h1>
      <p className="text-sm text-gray-600">
        {status.message ??
          "Nous améliorons notre service. L'application sera de nouveau disponible très bientôt."}
      </p>
      {status.until && (
        <p className="text-sm text-gray-600">
          Retour prévu : <strong>{formatDate(status.until)}</strong>
        </p>
      )}
      <p className="text-xs text-gray-400">
        Cette page se rechargera automatiquement.
      </p>
    </div>
    <Button variant="outline" onClick={onRetry} disabled={retrying}>
      <LuRefreshCw className={retrying ? "size-4 animate-spin" : "size-4"} />
      Réessayer
    </Button>
  </div>
);

const Banner: React.FC<{
  children: React.ReactNode;
  onDismiss?: () => void;
}> = ({ children, onDismiss }) => (
  <div
    role="status"
    className="fixed inset-x-0 top-0 z-[9998] flex items-center justify-center gap-3 bg-amber-100 px-4 pb-2 text-center text-sm text-amber-900"
    style={{ paddingTop: "calc(var(--safe-area-top, 0px) + 0.5rem)" }}
  >
    <LuConstruction className="size-4 shrink-0" />
    <span>{children}</span>
    {onDismiss && (
      <button aria-label="Fermer" onClick={onDismiss} className="shrink-0">
        <LuX className="size-4" />
      </button>
    )}
  </div>
);

/**
 * Wraps an app: replaces it with the maintenance page while the API is in
 * maintenance (from GET /maintenance on load, or any 503 MAINTENANCE later),
 * and announces an upcoming window with a banner.
 */
export const MaintenanceGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const status = useMaintenanceStore((s) => s.status);
  const setStatus = useMaintenanceStore((s) => s.setStatus);
  const [retrying, setRetrying] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem(BANNER_DISMISSED_KEY) === "1"
  );
  const bypass = getMaintenanceBypass();
  const blocked = !!status?.active && !bypass;

  const refresh = useCallback(async () => {
    const next = await fetchMaintenanceStatus();

    // Unreachable API: keep what we know rather than reopening the app
    if (!next) {
      return;
    }

    if (blocked && !next.active) {
      window.location.reload();

      return;
    }

    setStatus(next);
  }, [blocked, setStatus]);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!blocked) {
      return;
    }

    const timer = setInterval(() => void refresh(), POLL_INTERVAL);

    return () => clearInterval(timer);
  }, [blocked, refresh]);

  if (blocked) {
    return (
      <MaintenancePage
        status={status}
        retrying={retrying}
        onRetry={async () => {
          setRetrying(true);
          await refresh();
          setRetrying(false);
        }}
      />
    );
  }

  return (
    <>
      {status?.active && bypass && (
        <Banner>Maintenance active, accès de test (bypass)</Banner>
      )}
      {isUpcoming(status) && !bannerDismissed && (
        <Banner
          onDismiss={() => {
            sessionStorage.setItem(BANNER_DISMISSED_KEY, "1");
            setBannerDismissed(true);
          }}
        >
          {status.message ?? "Une maintenance est prévue"} : à partir du{" "}
          {formatDate(status.startsAt)}
          {status.until ? `, jusqu'au ${formatDate(status.until)}` : ""}.
        </Banner>
      )}
      {children}
    </>
  );
};
